import React, { useEffect, useRef, useState } from 'react';
import { TypedMap } from '../../types/TypedMap';
import { Message } from '../../types/Chats';
import { ChatSessionKey } from '../../types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import SendButton from './SendButton';
import useAutosizeTextArea from '../../hooks/useAutosizeTextArea';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { sendChatPostComment } from '../../actions/chat-actions';
import EmojiPicker from './EmojiPicker';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
}

const ChatPostCommentInput = ({ message, sessionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [commentContent, setCommentContent] = useState('');
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);

    const handleSend = () => {
        dispatch(
            sendChatPostComment(sessionKey, message.get('key'), commentContent),
        );
        setCommentContent('');
    };

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useAutosizeTextArea(textAreaRef, commentContent);

    useEffect(() => {
        if (cursorPosition !== null && textAreaRef.current) {
            textAreaRef.current.focus();
            textAreaRef.current.setSelectionRange(
                cursorPosition,
                cursorPosition,
            );
            setCursorPosition(null);
        }
    }, [cursorPosition, commentContent]);

    const updateMessage = (text: string, cursorPosition: number) => {
        setCommentContent(text);
        setCursorPosition(cursorPosition);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="px-6 pb-6">
            <form
                onSubmit={handleSend}
                className="border-gray-800 focus:border"
            >
                <div className="relative flex w-full items-start gap-x-3 rounded-sm border-gray-800 bg-gray-200 p-3 focus:border">
                    <textarea
                        id="chat-post-comment"
                        aria-label={i18next.t('chat.post.write.comment')}
                        className={twMerge(
                            'block h-fit max-h-24 w-full resize-none overflow-y-scroll bg-transparent pt-1 text-base text-wrap text-gray-800 placeholder:text-gray-500 focus:ring-0 focus:outline-hidden',
                        )}
                        onChange={(e) => setCommentContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        ref={textAreaRef}
                        rows={1}
                        value={commentContent}
                        placeholder={i18next.t('chat.post.write.comment')}
                    />
                    <div className="flex items-center gap-x-2">
                        <EmojiPicker
                            textAreaRef={textAreaRef}
                            messageContent={{ text: commentContent }}
                            updateMessage={updateMessage}
                        />
                        <SendButton
                            message={{ text: commentContent }}
                            handleSend={handleSend}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ChatPostCommentInput;
